<template>
  <div id="app" @click="handleGlobalClick" :data-theme="currentTheme">
    <nav-bar 
      :current-theme="currentTheme" 
      @theme-change="changeTheme"
      title="即答公考，面试极好"
      :title-style="{ color: 'gray' }"
      :is-logged-in="isLoggedIn"
      @logout="logOut"
      @show-feedback="showFeedback"
      @mobile-menu-action="handleMobileMenuClick"
    />

    <div class="container mt-3">
      <router-view @show-auth="showAuthModal = true" />
    </div>
    
    <!-- 添加ICP备案信息 -->
    <footer class="icp-footer">
      <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer">
        粤ICP备2025392434号
      </a>
    </footer>
    
    <auth-modal 
      :show="showAuthModal" 
      @close="handleModalClose"
      @login-success="onLoginSuccess"
      @login-failed="resetInteraction"
    />
    
    <!-- VIP会员模态框 -->
    <vip-modal
      :show="showVipModal"
      @close="showVipModal = false"
    />
  </div>
</template>

<script>
import AuthModal from './components/AuthModal.vue';
import VipModal from './components/VipModal.vue';
import NavBar from './components/layout/NavBar.vue';
import { mapGetters } from 'vuex';
import UserService from '@/services/user.service';

export default {
  name: 'App',
  components: {
    AuthModal,
    VipModal,
    NavBar
  },
  data() {
    return {
      showAuthModal: false,
      showVipModal: false,
      hasInteracted: false, // 标记用户是否与页面交互过
      authCheckCompleted: false, // 标记认证检查是否完成
      showMobileMenu: false,
      mobileMenuItems: []
    };
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'currentUser', 'currentTheme'])
  },
  created() {
    // 监听认证错误事件
    window.addEventListener('auth-error', this.handleAuthError);
    
    // 清除所有可能存在的会话cookie，确保完全使用基于令牌的身份验证
    document.cookie.split(";").forEach(c => {
      const cookie = c.trim();
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      if (name === "session") {
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        console.log("已清除会话cookie");
      }
    });
    
    // 在登录状态下尝试刷新令牌
    this.attemptTokenRefresh();
  },
  mounted() {
    // 应用挂载时检查并验证令牌
    this.verifyToken();
    
    // 检查用户是否已登录，避免在已登录状态下显示模态窗口
    this.authCheckCompleted = true;
    
    // 应用保存的主题
    document.body.setAttribute('data-theme', this.currentTheme);
    
    // 监听显示VIP模态框的事件
    window.addEventListener('show-vip-modal', () => {
      this.showVipModal = true;
    });
    
    // 监听auth-error事件，处理token过期情况
    window.addEventListener('auth-error', (event) => {
      // 先关闭所有其他弹窗，确保登录弹窗具有最高优先级
      this.showVipModal = false;
      
      // 如果有其他自定义弹窗，也在这里关闭
      // this.showOtherModal1 = false;
      // this.showOtherModal2 = false;
      
      // 显示登录弹窗
      this.showAuthModal = true;
    });
    
    // 创建事件总线并添加监听
    this.setupEventBus();
  },
  methods: {
    setupEventBus() {
      // 初始化事件总线
      this.$root.eventBus = {
        _events: {},
        on(event, callback) {
          if (!this._events[event]) this._events[event] = [];
          this._events[event].push(callback);
        },
        off(event, callback) {
          if (!this._events[event]) return;
          if (callback) {
            this._events[event] = this._events[event].filter(cb => cb !== callback);
          } else {
            this._events[event] = [];
          }
        },
        emit(event, ...args) {
          if (!this._events[event]) return;
          this._events[event].forEach(callback => {
            callback(...args);
          });
        }
      };
      
      // 添加监听show_vip_modal事件
      this.$root.eventBus.on('show_vip_modal', () => {
        console.log('接收到show_vip_modal事件');
        this.showVipModal = true;
      });
    },
    
    handleGlobalClick(event) {
      // 防止模态窗口已经显示的情况下再次触发
      if (this.showAuthModal) return;
      
      // 忽略导航栏中的点击事件
      if (event.target.closest('.navbar')) return;
      
      // 忽略链接点击事件，避免与路由冲突
      if (event.target.tagName === 'A' || event.target.closest('a')) return;
      
      // 检查当前路由，如果是PreMatch页面或AIMode页面，不自动弹出登录窗口
      const currentRoute = this.$route.path;
      if (currentRoute === '/pre-match' || currentRoute === '/aimode') {
        // 在这些页面中，不在首次加载时强制登录
        // 组件内部会在用户交互时处理登录
        return;
      }
      
      // 仅在未登录状态且完成认证检查后显示模态窗口
      if (!this.isLoggedIn && this.authCheckCompleted && !this.hasInteracted) {
        this.showAuthModal = true;
        this.hasInteracted = true; // 标记用户已经交互过，防止重复弹窗
      }
    },
    logOut() {
      this.$store.dispatch('logout');
      this.$router.push('/');
      // 重置交互标记，使得在登出后点击可以再次触发登录窗口
      this.hasInteracted = false;
    },
    onLoginSuccess() {
      // 在登录成功后刷新当前路由
      window.location.reload();
    },
    handleModalClose() {
      this.showAuthModal = false;
      
      // 如果用户关闭模态窗口但仍未登录，重置交互标志，以便再次点击时弹出登录窗口
      if (!this.isLoggedIn) {
        this.resetInteraction();
      }
    },
    
    resetInteraction() {
      // 重置交互标志，允许再次弹出登录窗口
      this.hasInteracted = false;
    },
    
    changeTheme(theme) {
      this.$store.dispatch('changeTheme', theme);
    },
    
    showFeedback() {
      alert('请发送反馈至: jidagk@163.com');
    },
    
    handleMobileMenuClick(item) {
      // 实现移动端菜单点击处理逻辑
      console.log(`移动端菜单点击: ${item}`);
      
      switch(item) {
        case 'match':
          if (this.isLoggedIn) {
            this.$router.push('/pre-match');
          } else {
            this.showAuthModal = true;
          }
          break;
        case 'vip':
          if (this.isLoggedIn) {
            this.showVipModal = true;
          } else {
            this.showAuthModal = true;
          }
          break;
      }
    },
    // 验证令牌方法
    verifyToken() {
      // const user = AuthService.getCurrentUser();
      // if (user && user.token) {
      //   // 从本地存储中获取到token后，发送请求验证其有效性
      //   AuthService.verifyTokenOnStartup()
      //     .then(response => {
      //       console.log('令牌验证成功，用户已自动登录');
      //     })
      //     .catch(error => {
      //       console.error('令牌验证失败:', error);
      //       // axios拦截器会自动处理444状态码
      //     });
      // }
    },
    handleAuthError(event) {
      // 处理认证错误事件
      console.log('接收到auth-error事件', event.detail);
      
      // 先关闭所有其他弹窗，确保登录弹窗具有最高优先级
      this.showVipModal = false;
      
      // 显示登录弹窗
      this.showAuthModal = true;
      
      if (event.detail && event.detail.message) {
        // 可以在这里显示错误消息
        console.log('认证错误:', event.detail.message);
      }
    },
    attemptTokenRefresh() {
      // 检查用户是否已登录
      if (this.isLoggedIn && this.$store.state.user) {
        const user = this.$store.state.user;
        // 这里可以添加令牌到期检查逻辑
        
        // 尝试刷新令牌
        this.$store.dispatch('refreshToken')
          .then(result => {
            console.log('令牌刷新成功');
          })
          .catch(error => {
            console.error('令牌刷新失败:', error);
            // 如果是认证错误，可能需要提示用户重新登录
            if (error && error.isAuthError) {
              this.showAuthModal = true;
            }
          });
      }
    },
  }
};
</script>

<style>
/* 全局样式 */
* {
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  background-color: var(--bg-color);
  color: var(--text-color);
  transition: background-color 0.3s, color 0.3s;
}

/* 定义全局变量 */
:root {
  --bg-color: #ffffff;
  --text-color: #333333;
  --border-color: #dddddd;
  --hover-color: #f5f5f5;
  --accent-color: #4a8bfc;
}

/* 黑夜模式变量 */
[data-theme="dark"] {
  --bg-color: #121212;
  --text-color: #eeeeee;
  --border-color: #444444;
  --hover-color: #2a2a2a;
  --accent-color: #5c96ff;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.5;
}

#app {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'PingFang SC', 'Helvetica Neue', Helvetica, 'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-color);
  background-color: var(--bg-color);
}

.container {
  flex: 1;
  padding: 0 15px;
  margin: 0 auto;
  background-color: transparent;
}

/* ==================== */
/* 移动端优化 */
@media (max-width: 768px) {
  html {
    font-size: 14px;
  }
  
  input, button, select, textarea {
    font-size: 16px !important;
  }
  
  button, 
  [role="button"], 
  input[type="button"], 
  input[type="submit"], 
  input[type="reset"] {
    min-height: 44px;
    min-width: 44px;
  }
  
  .scrollable-container {
    -webkit-overflow-scrolling: touch;
  }
  
  .mobile-nav-container {
    display: flex;
  }
  
  .desktop-only {
    display: none !important;
  }
  
  .mobile-only {
    display: block !important;
  }
  
  .has-bottom-nav {
    padding-bottom: 60px;
  }
}

/* ==================== */
/* 黑夜模式样式 */
[data-theme="dark"] .container {
  background-color: transparent;
}

[data-theme="dark"] .card {
  background: #2d2d2d;
  color: #f8f9fa;
  border-color: #444;
}

[data-theme="dark"] .card-header {
  background: #383838;
  border-color: #444;
}

[data-theme="dark"] .form-control {
  background: #2d2d2d;
  color: #f8f9fa;
  border-color: #444;
}

[data-theme="dark"] .form-control:focus {
  background: #2d2d2d;
  color: #f8f9fa;
}

[data-theme="dark"] .btn-secondary {
  background-color: #495057;
  border-color: #495057;
}

[data-theme="dark"] .text-muted {
  color: #adb5bd !important;
}

[data-theme="dark"] .table {
  color: #f8f9fa;
}

[data-theme="dark"] .table th,
[data-theme="dark"] .table td {
  border-color: #444;
}

/* 保持导航栏在所有主题中为暗色 */
.navbar-dark {
  background-color: #343a40 !important;
}

/* ICP备案信息样式 */
.icp-footer {
  text-align: center;
  padding: 15px 0;
  font-size: 0.8rem;
  color: #999;
  margin-top: 50px;
  background-color: transparent;
}

.icp-footer a {
  color: #999;
  text-decoration: none;
}

/* 黑夜主题下的ICP页脚样式 */
[data-theme="dark"] .icp-footer,
[data-theme="dark"] .icp-footer a {
  color: #666;
  background-color: transparent;
}
</style>
